import React, { useEffect, useState, Suspense, useRef } from 'react';
import { Route, Routes, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { lazy } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import TopBar from '@components/TopBar';
import BottomMenu from '@components/BottomMenu';
import PromoBanner from '@components/PromoBanner';
import SignupSuccess from '@components/SignupSuccess';
import Login from '@pages/Login';
import Signup from '@pages/Signup';
import Landing from '@pages/Landing';
import Home from '@pages/Home';
import Custom from '@pages/Custom';
import Favourite from '@pages/Favourite';
import Enquiry from '@pages/Enquiry';
import Order from '@pages/Order';
import Profile from '@pages/Profile';
import UserProfile from '@pages/UserProfile';
import OrderHistory from '@pages/OrderHistory';
import ImageStudio from '@pages/ImageStudio';
import Support from '@pages/Support';
import UserChat from '@pages/UserChat';
import AdminChat from '@pages/AdminChat';
import Meta from '@pages/Meta';
import Engage from '@pages/Engage';
import Organize from '@pages/Organize';
import DelRoute from '@pages/DelRoute';
import Callback from '@pages/Callback';
import VerifyEmailSuccess from '@pages/VerifyEmailSuccess';
import SharedView from '@pages/SharedView';
import Settings from '@pages/Settings';
import AppShare from '@pages/AppShare';
import MaintenanceScreen from '@components/MaintenanceScreen';
import { getFirebase, setCurrentUserEnv, getCurrentUserEnv, getProdDb } from '@services/firebase';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { readGlobalProfiles, updateGlobalProfile } from '@services/firebaseGlobal';
import { signOut } from 'firebase/auth';

// Import the Google Drive Provider
import { GoogleDriveProvider } from './contexts/GoogleDriveContext';
import { useAlert } from './contexts/ToastContext';

// Lazy-load heavy pages
const HouseKeeping = lazy(() => import('@pages/HouseKeeping'));
const UserAdmin = lazy(() => import('@pages/UserAdmin'));
const Analytics = lazy(() => import('@pages/Analytics'));
const JobWizard = lazy(() => import('@pages/JobWizard'));

const { auth, db } = getFirebase();
const prodDb = getProdDb(); // always Prod – used for cUsers and siteConfig

const AppComponent = () => {
  const [user, loading, error] = useAuthState(auth);
  const navigate = useNavigate();
  const location = useLocation();
  const showAlert = useAlert();
  const [wasActive, setWasActive] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showExitToast, setShowExitToast] = useState(false);
  const backPressedOnceRef = useRef(false);
  const [userRole, setUserRole] = useState(() => {
    // Initialize from localStorage to avoid flicker on refresh
    try {
      const cachedRole = localStorage.getItem('userRole');
      return cachedRole || null;
    } catch (err) {
      console.error('Error reading userRole from localStorage:', err);
      return null;
    }
  });
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isGapiLoaded, setIsGapiLoaded] = useState(false);
  const [appDown, setAppDown] = useState(false);
  const initializedRef = useRef(false);
  // Tracks the env seen on previous snapshot — null means first fire (initial load)
  const prevEnvRef = useRef(null);

  // Real-time listener for app maintenance/down status
  useEffect(() => {
    const statusRef = doc(prodDb, 'siteConfig', 'appStatus');
    const unsub = onSnapshot(statusRef, (snap) => {
      if (snap.exists()) {
        setAppDown(!!snap.data().isDown);
      } else {
        setAppDown(false);
      }
    }, () => setAppDown(false));
    return () => unsub();
  }, []);

  // Real-time listener for user's isflag status, role and env
  useEffect(() => {
    if (user) {
      const userDocRef = doc(prodDb, 'cUsers', user.uid);
      const unsubscribe = onSnapshot(userDocRef, (docSnap) => {
        if (docSnap.exists()) {
          const userData = docSnap.data();
          if (!userData.isflag && userData.first_login == false) {
            signOut(auth)
              .then(() => {
                showAlert('Your account has been marked invalid. You have been logged out.');
                navigate('/login', { replace: true });
              })
              .catch((err) => {
                console.error('Error signing out:', err.message, err.stack);
              });
            return;
          }
          // Sync role in real-time when changed by an admin
          let role = 'User';
          if (userData.isadminuser) role = 'Admin';
          else if (userData.issuperuser) role = 'Super';
          else if (userData.ismover) role = 'Mover';
          else if (userData.isbuyer) role = 'Buyer';
          setUserRole(role);
          try { localStorage.setItem('userRole', role); } catch (_) {}

          // Sync env — reload the page when env changes so all Firestore
          // listeners reconnect to the correct database instance.
          const newEnv = userData.env || 'Prod';
          // Read the env firebase.js was initialised with BEFORE overwriting it.
          // On the very first snapshot (prevEnvRef === null) this equals what
          // firebase.js resolved from localStorage at page-load time.
          const initializedEnv = getCurrentUserEnv();
          setCurrentUserEnv(newEnv);
          try { localStorage.setItem('userEnv', newEnv); } catch (_) {}
          if (initializedEnv !== newEnv || (prevEnvRef.current !== null && prevEnvRef.current !== newEnv)) {
            window.location.reload();
            return;
          }
          prevEnvRef.current = newEnv;
        } else {
          console.warn('cUsers document not found for UID:', user.uid);
        }
      }, (err) => {
        console.error('Error in real-time listener for isflag:', err.message, err.stack);
      });

      return () => unsubscribe();
    }
  }, [user, navigate]);

  // Initialize Google API client
  useEffect(() => {
    let retryCount = 0;
    const MAX_RETRIES = 5;

    const initializeGapiClient = () => {
      if (initializedRef.current || retryCount >= MAX_RETRIES) {
        return;
      }
      initializedRef.current = true;
      if (!window.gapi) {
        console.error('window.gapi is undefined. Retrying in 2 seconds...');
        retryCount++;
        initializedRef.current = false; // Reset for retry
        setTimeout(initializeGapiClient, 2000);
        return;
      }
      window.gapi.load('client', () => {
        window.gapi.client
          .init({
            discoveryDocs: [
              'https://www.googleapis.com/discovery/v1/apis/drive/v3/rest',
              'https://sheets.googleapis.com/$discovery/rest?version=v4',
            ],
          })
          .then(() => {
            setIsGapiLoaded(true);
          })
          .catch(err => {
            console.error('Error initializing Google API client:', JSON.stringify(err, null, 2));
            retryCount++;
            initializedRef.current = false; // Reset for retry
            setTimeout(initializeGapiClient, 2000);
          });
      });
    };

    initializeGapiClient();
  }, []);

  useEffect(() => {
  }, [isGapiLoaded]);

  // Sync admin-set currency/language from Firestore to localStorage for all users
  useEffect(() => {
    if (!prodDb) return;
    const settingsRef = doc(prodDb, 'siteConfig', 'appSettings');
    const unsub = onSnapshot(settingsRef, (snap) => {
      if (snap.exists()) {
        const d = snap.data();
        if (d.currency) localStorage.setItem('appCurrency', d.currency);
        if (d.language) localStorage.setItem('appLanguage', d.language);
        if (d.country) {
          localStorage.setItem('appCountry', d.country);
          window.dispatchEvent(new CustomEvent('appCountryChanged'));
        }
      }
    });
    return () => unsub();
  }, []);

  // Remove the old fetchGoogleSheets function since it's now handled by context

  // Fetch user role and sync with localStorage
  useEffect(() => {
    const fetchUserRole = async () => {
      if (user) {
        try {
          const userList = await readGlobalProfiles('cUsers');
          const currentUserData = userList.find(userData => userData.id === user.uid);
          if (currentUserData) {
            let role = 'User';
            if (currentUserData.isadminuser) {
              role = 'Admin';
            } else if (currentUserData.issuperuser) {
              role = 'Super';
            } else if (currentUserData.ismover) {
              role = 'Mover';
            } else if (currentUserData.isbuyer) {
              role = 'Buyer';
            }
            setUserRole(role);
            // Set env for data routing (Prod by default if field absent)
            const loginEnv = currentUserData.env || 'Prod';
            setCurrentUserEnv(loginEnv);
            try { localStorage.setItem('userEnv', loginEnv); } catch (_) {}
            // Cache role in localStorage
            try {
              localStorage.setItem('userRole', role);
            } catch (err) {
              console.error('Error saving userRole to localStorage:', err);
            }
          } else {
            console.warn('No user data found for UID:', user.uid, 'Setting default role to User');
            const defaultUserData = {
              isactive: false,
              isuser: true,
              isadminuser: false,
              issuperuser: false,
              ismover: false,
              isbuyer: false,
              last_login: new Date().toISOString(),
              first_login: true,
              isflag: false,
              userId: user.uid,
              env: 'Prod',
              created: serverTimestamp(),
            };
            await setDoc(doc(prodDb, 'cUsers', user.uid), defaultUserData);
            setUserRole('User');
            try {
              localStorage.setItem('userRole', 'User');
            } catch (err) {
              console.error('Error saving userRole to localStorage:', err);
            }
          }
        } catch (err) {
          console.error('Error fetching/creating user role:', err.message, err.stack);
          setUserRole('User');
          try {
            localStorage.setItem('userRole', 'User');
          } catch (err) {
            console.error('Error saving userRole to localStorage:', err);
          }
        }
      } else {
        setUserRole(null);
        try {
          localStorage.removeItem('userRole');
        } catch (err) {
          console.error('Error clearing userRole from localStorage:', err);
        }
      }
    };

    fetchUserRole();
  }, [user]);

  // Redirect to login if user is not authenticated, but only after Firebase Auth loading is complete
  useEffect(() => {
    if (loading) return; // Wait for Firebase Auth to finish loading
    if (!user && !error) {
      const path = window.location.pathname.split('?')[0];
      // Clear stale cache so the app cannot be used without a valid session
      try { localStorage.removeItem('userRole'); } catch (e) {}
      const timer = setTimeout(() => {
        if (
          path !== '/' &&            // logged-out visitors see the marketing Landing at /
          path !== '/login' &&
          path !== '/signup' &&
          path !== '/signup-success' &&
          path !== '/verify-email-success' &&
          path !== '/verify-email' &&
          path !== '/shared'
        ) {
          navigate('/login', { replace: true });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [user, loading, error, navigate]);

  // Push notifications for every signed-in person, whatever page they open first (Movers never see
  // Home, where this used to happen only). Native app: always — the app asks the OS itself; the
  // browser's Notification permission does not exist inside the app. Web: once permission is given.
  useEffect(() => {
    if (!user || !userRole) return;
    import('@services/fcmService').then(({ registerFCMToken, isNativeApp }) => {
      const webGranted = typeof Notification !== 'undefined' && Notification.permission === 'granted';
      if (isNativeApp() || webGranted) registerFCMToken(user.uid, userRole);
    }).catch(() => undefined);
  }, [user, userRole]);

  // Reset search query when navigating away from Home
  useEffect(() => {
    if (location.pathname !== '/' && location.pathname !== '/shared') {
      setSearchQuery('');
    }
  }, [location.pathname]);

  // Update last_seen field and isactive flag on route change (debounced)
  useEffect(() => {
    if (!user) return;

    const updateLastSeen = async () => {
      try {
        const now = new Date();
        const lastUpdateTime = lastUpdate ? new Date(lastUpdate) : null;
        const timeSinceLastUpdate = lastUpdateTime ? (now - lastUpdateTime) / 1000 : Infinity;

        if (timeSinceLastUpdate >= 60) {
          const updatePayload = {
            last_seen: now.toISOString(),
          };
          if (!isActive) {
            updatePayload.isactive = true;
            await updateGlobalProfile('cUsers', user.uid, updatePayload);
            setLastUpdate(now);
          }
          if (isActive) {
            setIsActive(true);
            setWasActive(true);
          }
        }
      } catch (err) {
        console.error('Error updating last_seen:', err.message, err.stack);
      }
    };

    updateLastSeen();
  }, [user, location.pathname, lastUpdate, isActive]);

  // Android hardware back button — exit on home screen, go back otherwise
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const listenerPromise = CapacitorApp.addListener('backButton', ({ canGoBack }) => {
      if (location.pathname === '/' || !canGoBack) {
        CapacitorApp.exitApp();
      } else {
        window.history.back();
      }
    });

    return () => {
      listenerPromise.then(listener => listener.remove());
    };
  }, [location.pathname]);

  // PWA back button — trap on home with double-back-to-exit toast
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;

    // Push a sentinel state so the browser always has something to pop
    window.history.pushState({ pwaHome: true }, '');

    const handlePopState = () => {
      if (location.pathname === '/') {
        // Re-push so they can't navigate out of the PWA via back
        window.history.pushState({ pwaHome: true }, '');

        if (backPressedOnceRef.current) {
          // Second press within 2s — attempt close (works if PWA was launched as standalone)
          window.close();
        } else {
          backPressedOnceRef.current = true;
          setShowExitToast(true);
          setTimeout(() => {
            backPressedOnceRef.current = false;
            setShowExitToast(false);
          }, 2000);
        }
      } else {
        window.history.back();
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [location.pathname]);

  const handleSearchChange = (query) => {
    setSearchQuery(query);
  };

  // Maintenance/down screen takes priority over everything — shown even during auth loading
  if (appDown) {
    return <MaintenanceScreen />;
  }

  // Show a blank screen while Firebase Auth is resolving to prevent content flicker
  if (loading) {
    return (
      <div style={{ width: '100vw', height: '100vh', background: '#fff' }} />
    );
  }

  return (
    <GoogleDriveProvider>
      <div className="app">
        {showExitToast && (
          <div style={{
            position: 'fixed', bottom: '80px', left: '50%', transform: 'translateX(-50%)',
            background: 'rgba(0,0,0,0.75)', color: '#fff', padding: '10px 22px',
            borderRadius: '20px', fontSize: '14px', zIndex: 9999, pointerEvents: 'none',
            whiteSpace: 'nowrap'
          }}>
            Press back again to exit
          </div>
        )}
        {location.pathname !== '/imagestudio' && !(!user && location.pathname === '/') && <TopBar onSearchChange={handleSearchChange} userRole={userRole} />}
        {((user && location.pathname === '/') || location.pathname === '/shared') && <PromoBanner userRole={userRole} />}
        {error ? (
          <div>Error: {error.message}</div>
        ) : (
          <Routes>
            <Route path="/login" element={!loading && user ? <Navigate to="/" replace /> : <Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/signup-success" element={<SignupSuccess />} />
            <Route path="/user-chat" element={<UserChat userRole={userRole} />} />
            <Route path="/admin-chat" element={<AdminChat userRole={userRole} />} />
            <Route path="/custom" element={<Custom userRole={userRole} searchQuery={searchQuery} />} />
            <Route path="/favourite" element={<Favourite userRole={userRole} />} />
            <Route path="/enquiry" element={<Enquiry userRole={userRole} />} />
            <Route path="/order" element={<Order userRole={userRole} />} />
            <Route path="/profile" element={<Profile userRole={userRole} />} />
            <Route path="/user-profile" element={<UserProfile userRole={userRole} />} />
            <Route path="/user-admin" element={
              userRole === 'Super' || userRole === 'Admin' ? 
              (
                <UserAdmin userRole={userRole} />
              ) : (
                <Navigate to="/login" replace />
              )
            } />
            <Route path="/app-share" element={
              userRole === 'Super' || userRole === 'Admin' ?
                <AppShare userRole={userRole} />
              : <Navigate to="/login" replace />
            } />
            <Route path="/analytics" element={
              <Suspense fallback={<div>Loading Analytics...</div>}>
                <Analytics userRole={userRole} />
              </Suspense>
            } />
            <Route path="/order-history" element={
              userRole === 'Buyer' ? (
                <OrderHistory userRole={userRole} />
              ) : (
                <Navigate to="/login" replace />
              )
            } />
            <Route path="/imagestudio" element={<ImageStudio userRole={userRole} />} />
            <Route path="/support" element={<Support userRole={userRole} />} />
            <Route
              path="/meta"
              element={
                userRole === 'Super' || userRole === 'Admin' ? (
                  <Meta userRole={userRole} />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/housekeeping"
              element={
                userRole === 'Admin' ? (
                  <Suspense fallback={<div>Loading HouseKeeping...</div>}>
                    <HouseKeeping userRole={userRole} />
                  </Suspense>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/engage"
              element={
                userRole === 'Mover' || userRole === 'Admin' ? (
                  <Engage userRole={userRole} />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/organize"
              element={
                userRole === 'Mover' || userRole === 'Admin' || userRole === 'Super' ? (
                  <Organize userRole={userRole} />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/delroute"
              element={
                userRole === 'Mover' ? (
                  <DelRoute userRole={userRole} />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/callback"
              element={
                <Callback />
              }
            />
            <Route
              path="/job-wizard"
              element={
                userRole === 'Admin' ? (
                  <Suspense fallback={<div>Loading JobWizard...</div>}>
                    <JobWizard userRole={userRole} />
                  </Suspense>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route path="/verify-email-success" element={<VerifyEmailSuccess />} />
            <Route path="/shared" element={<SharedView searchQuery={searchQuery} />} />
            <Route path="/settings" element={<Settings userRole={userRole} />} />
            <Route path="/" element={user ? <Home searchQuery={searchQuery} userRole={userRole} /> : <Landing />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )}
        {userRole && location.pathname !== '/imagestudio' && <BottomMenu userRole={userRole} />}
      </div>
    </GoogleDriveProvider>
  );
};

export default AppComponent;